'use client'

import React from 'react'

import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag'

type ForwardProps = Omit<React.HTMLAttributes<HTMLElement>, 'children'>

type FlagGateProps = {
  readonly flagKey?: string
  readonly children: React.ReactNode
} & ForwardProps

type FlagGateWithFlagProps = {
  readonly flagKey: string
  readonly children: React.ReactNode
} & ForwardProps

function renderChildWithProps(
  children: React.ReactNode,
  props: ForwardProps,
): React.ReactNode {
  if (!React.isValidElement(children)) return children

  // Merge className from parent and child without losing either
  const incomingClassName =
    typeof props.className === 'string' ? props.className : undefined
  const childProps = children.props as Record<string, unknown>
  const childClassNameValue = childProps.className
  const childClassName =
    typeof childClassNameValue === 'string' ? childClassNameValue : undefined
  const mergedClassName = [childClassName, incomingClassName]
    .filter((value) => Boolean(value))
    .join(' ')

  const finalProps: ForwardProps = {
    ...props,
    className: mergedClassName || undefined,
  }

  return React.cloneElement(children, finalProps)
}

function FlagGateWithFlag({
  flagKey,
  children,
  ...rest
}: FlagGateWithFlagProps) {
  // Ensure SSR and the first client render match by not rendering until after mount
  const [hasMounted, setHasMounted] = React.useState(false)
  React.useEffect(() => {
    setHasMounted(true)
  }, [])

  const enabled = usePostHogFeatureFlag(flagKey)

  // During SSR and the first client render, render nothing to avoid hydration mismatches
  if (!hasMounted) return null

  // Only render when the flag is explicitly enabled
  if (enabled !== true) return null

  return renderChildWithProps(children, rest)
}

export function FlagGate({ flagKey, children, ...rest }: FlagGateProps) {
  if (!flagKey) return renderChildWithProps(children, rest)
  return (
    <FlagGateWithFlag flagKey={flagKey} {...rest}>
      {children}
    </FlagGateWithFlag>
  )
}
